import { MAP_QUERY_REFERRER } from 'pages/map-query/document-link';
import { withReferrer } from 'utils/referrer';

/**
 * The page's ?referrer= as a query string to carry onto another document tab.
 * @param {string} searchParams The current query string.
 * @return {string} `?referrer=…`, or empty without one.
 */
const getReferrerAsSearchParam = (searchParams: string) => {
  const params = new URLSearchParams(searchParams);
  const referrer = params.has('referrer')
    ? `?referrer=${params.get('referrer')}`
    : '';

  return referrer;
};

/**
 * A query string holding one param plus the page's ?referrer=, if any.
 * @param {string} searchParams The current query string.
 * @param {string} name The param to set.
 * @param {string} value Its value.
 * @return {string} The query string, without a leading `?`.
 */
const addSearchParamToReferrer = (
  searchParams: string,
  name: string,
  value: string
) => {
  const oldParams = new URLSearchParams(searchParams);
  const newParams = new URLSearchParams();

  newParams.append(name, value);
  const referrer = oldParams.get('referrer');
  if (referrer) newParams.append('referrer', referrer);

  return newParams.toString();
};

export const DOCUMENTS_COUNT_API = {
  COUNT: '/queries/documents/count',
} as const;

// The only entity referrer honoured: a same-site entity page, never a full URL.
const ENTITY_REFERRER = /^\/entities\/\d+$/;

/**
 * Builds the link tagger for one entity page. That page opens on its Documents
 * tab, so the entity href alone returns the reader there.
 * @param {string} entityHref The entity page, e.g. /entities/1564.
 * @return {Function} Tags a document link with that page.
 */
export function withEntityReferrer(entityHref: string) {
  return (href: string): string => withReferrer(href, entityHref);
}

export interface DocumentRootCrumb {
  href: string;
  label: string;
  // An entity page, whose name the crumb shows once it loads (label until then).
  entity: boolean;
}

/**
 * The document page's root breadcrumb for the page it was opened from. Only a
 * same-site Map Query or entity path is honoured, never an arbitrary referrer.
 * @param {string | null} referrer The ?referrer= value.
 * @param {string | null} dataset The ?dataset= value (Map Query's tab).
 * @return {DocumentRootCrumb} The crumb.
 */
export function documentRootCrumb(
  referrer: string | null,
  dataset: string | null
): DocumentRootCrumb {
  if (referrer === MAP_QUERY_REFERRER) {
    return {
      href: dataset
        ? `${MAP_QUERY_REFERRER}?dataset=${dataset}`
        : MAP_QUERY_REFERRER,
      label: 'Map Query',
      entity: false,
    };
  }
  if (referrer && ENTITY_REFERRER.test(referrer)) {
    return { href: referrer, label: 'Entity', entity: true };
  }
  return { href: '/documents', label: 'Search', entity: false };
}

export { getReferrerAsSearchParam, addSearchParamToReferrer };
